import { Saga } from 'redux-saga'
import {
  ActionPattern,
  all,
  call,
  put,
  select,
  takeLatest,
} from 'redux-saga/effects'

import ApiSagas from '../api/sagas'
import { Onboarding } from '../../graphql/Services'
import { actions, selectors } from '..'
import { ServiceRequestAction } from '../../helpers/ReduxHelpers'
import { onboard } from '../../graphql/Services/User/mutations'
import {
  planCompleteCarbohydrate,
  planDriedVegetable,
  planExpress,
  planFish,
  planFruitAndVegetable,
  planMeat,
  planMoment,
  planRegimeValues,
  planStructure,
  planSugaryProduct,
} from '../../models/Planning'
import { User } from '../../types/fam-graphql'

export default class OnboardingSagas {
  static *init() {
    if (!process.browser) {
      yield put(actions.app.setIsLoading(true))
    } else {
      yield put(actions.app.setIsLoading(false))
    }
  }
  static *loadTips() {
    const result = yield call(
      ApiSagas.query,
      Onboarding.queries.getOnboardingTips()
    )

    yield put(actions.onboarding.setTips(result?.data))

    if (!result) {
      return { error: 404 }
    }

    return
  }

  static *saveOnboardingValues({
    payload: { steps, currentStepIndex },
  }: {
    payload: { steps: any; currentStepIndex: number }
  }) {
    const output = steps.splice(0, currentStepIndex)
    const toKeepValues = output.map((a: any) => a.name)
    const onboardingValues = yield select(selectors.onboarding.stepsValues)
    const objectives: any = { ...onboardingValues }

    toKeepValues.forEach((key: string) => {
      if (onboardingValues[key]) {
        objectives[key] = onboardingValues[key]
      }
    })
  }

  static *onboardHandler(action: ServiceRequestAction) {
    const { steps, currentStepIndex } = action.payload?.params
    yield put(actions.user.setIsGeneratingPlanning(true))

    const output = steps.slice(0, currentStepIndex)
    const toKeepValues = output.map((a: any) => a.name)
    const onboardingValues = yield select(selectors.onboarding.stepsValues)
    const objectives: any = { ...onboardingValues }

    toKeepValues.forEach((key: string) => {
      if (onboardingValues[key]) {
        objectives[key] = onboardingValues[key]
      }
    })

    // Retrieve nutritional informations
    const input = yield call(OnboardingSagas.getOnboardPayload, objectives)

    const results = yield call(ApiSagas.mutate, onboard(input))

    if (!results.error) {
      yield put(actions.onboarding.onboardSuccess({}))
      yield put(actions.user.setUser(results.data as User))
      yield put(actions.user.setHasJustModifiedExpressPref('onboard'))
      yield put(actions.user.generatePlanning())
    } else {
      yield put(actions.onboarding.onboardError(results.error))
    }
  }

  static getOnboardPayload(userNutritionalValues: any) {
    return {
      preferences: {
        portions: userNutritionalValues?.people_count ?? 2,
        regime: planRegimeValues(userNutritionalValues?.diet_practice),
        structure: planStructure(userNutritionalValues?.course_composition),
        express: planExpress(userNutritionalValues?.express),
        moment: planMoment(userNutritionalValues?.moment),
      },
      objectives: {
        fruit_and_vegetable: planFruitAndVegetable(
          userNutritionalValues?.fruits_vegetables_quantity
        ),
        complete_carbohydrate: planCompleteCarbohydrate(
          userNutritionalValues?.starchy_foods_frequency
        ),
        dried_vegetable: planDriedVegetable(
          userNutritionalValues?.dried_vegetables_frequency
        ),
        meat: planMeat(userNutritionalValues?.meat_frequency),
        fish: planFish(userNutritionalValues?.fish_frequency),
        sugary_product: planSugaryProduct(
          userNutritionalValues?.drink_frequency
        ),
      },
    }
  }

  static *loop() {
    yield all([
      takeLatest(
        actions.onboarding.saveOnboardingValues as ActionPattern,
        this.saveOnboardingValues as Saga
      ),
      takeLatest(
        actions.onboarding.onboardRequest as ActionPattern,
        this.onboardHandler as Saga
      ),
    ])
  }
}
